import { XMarkIcon } from '@heroicons/react/16/solid';
import { useCallback, useEffect, useState, type JSX } from 'react';
import SelectTable from '../data-table/custom-tables/select-table';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { Filter, FilterValue } from './constants';

interface RelationModalProps {
  filter: Filter;
  initialValue: FilterValue;
  onDone: (val: string[]) => void;
  TableFilters: ({
    clientState,
    filters,
    tableId,
  }: {
    clientState?: boolean;
    filters: Filter[];
    tableId: string;
  }) => JSX.Element;
}

export function RelationModal({
  filter,
  initialValue,
  onDone,
  TableFilters,
}: RelationModalProps) {
  const [value, setValue] = useState<string[] | null>(null);

  useEffect(() => {
    if (Array.isArray(initialValue)) {
      setValue(initialValue);
      return;
    }
    setValue(null);
  }, [initialValue]);

  const updateValue = useCallback(
    (selected: string) => {
      if ((value ?? []).includes(selected)) {
        setValue((value ?? []).filter((v) => v !== selected));
      } else {
        setValue([...(value ?? []), selected]);
      }
    },
    [value]
  );

  const handleDone = useCallback(
    (val: string[] | null) => onDone(val ?? []),
    [onDone]
  );

  if (!filter.relationModelName) {
    return null;
  }

  return (
    <Dialog key='0' defaultOpen onOpenChange={() => handleDone(value)}>
      <DialogContent className='min-w-150 gap-0 p-0'>
        <DialogHeader>
          <DialogTitle>Välj filtrering</DialogTitle>
          <Button
            onClick={() => handleDone(value)}
            variant='outline'
            size='icon-sm'
          >
            <XMarkIcon className='size-4' />
          </Button>
          <DialogDescription className='sr-only'>
            Filtrera på {filter.title}
          </DialogDescription>
        </DialogHeader>
        <div className='max-h-[min(800px,calc(var(--window-keeper-height)-175px))] min-h-125 min-w-150 overflow-y-auto'>
          <SelectTable
            tableId={`${filter.relationModelName}`}
            onSelected={updateValue}
            TableFilters={TableFilters}
            maxSelectedItems={filter.maxSelectedItems}
            isMultiSelect
            queryParams={
              filter.relationDefaultFilters
                ? { filters: filter.relationDefaultFilters }
                : undefined
            }
            selected={value ?? []}
            modelName={filter.relationModelName}
          />
        </div>
        <DialogFooter>
          <Button onClick={() => handleDone(value)}>Klar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
